import { useEffect, useMemo, useState } from 'react';
import { admin, type PermissionsMatrix as Matrix } from '@/api/endpoints';
import { Icon } from '@/components/Icon';

export function PermissionsMatrix() {
  const [data, setData] = useState<Matrix | null>(null);
  const [draft, setDraft] = useState<Record<string, Record<string, boolean>>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const m = await admin.permissions.matrix();
      setData(m);
      setDraft(JSON.parse(JSON.stringify(m.matryca)));
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message });
    } finally { setLoading(false); }
  }

  function toggle(role: string, upr: string) {
    setDraft(d => ({
      ...d,
      [role]: { ...d[role], [upr]: !d[role]?.[upr] },
    }));
  }

  // ── Zarządzanie rolami (dynamiczne role) ──────────────────────────────
  const [showAddRole, setShowAddRole] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newGV, setNewGV] = useState(false);

  async function addRole() {
    const code = newCode.trim().toUpperCase();
    if (!code || !newName.trim()) { setMsg({ type: 'err', text: 'Podaj kod i nazwę roli' }); return; }
    setBusy(true); setMsg(null);
    try {
      await admin.permissions.createRole({ code, displayName: newName.trim(), globalViewer: newGV });
      setMsg({ type: 'ok', text: 'Utworzono rolę ' + code });
      setNewCode(''); setNewName(''); setNewGV(false); setShowAddRole(false);
      await load();
    } catch (e: any) { setMsg({ type: 'err', text: e.message }); } finally { setBusy(false); }
  }

  async function delRole(code: string) {
    if (!window.confirm('Usunąć rolę „' + code + '"? Uprawnienia tej roli zostaną skasowane.')) return;
    setBusy(true); setMsg(null);
    try {
      await admin.permissions.removeRole(code);
      setMsg({ type: 'ok', text: 'Usunięto rolę ' + code });
      await load();
    } catch (e: any) { setMsg({ type: 'err', text: e.message }); } finally { setBusy(false); }
  }

  async function toggleGlobalViewer(code: string, current: boolean) {
    setBusy(true); setMsg(null);
    try {
      await admin.permissions.updateRole(code, { globalViewer: !current });
      await load();
    } catch (e: any) { setMsg({ type: 'err', text: e.message }); } finally { setBusy(false); }
  }

  async function save() {
    if (!data) return;
    setBusy(true); setMsg(null);
    try {
      const zmiany: { role: string; permission: string; allowed: boolean }[] = [];
      for (const role of Object.keys(draft)) {
        for (const upr of Object.keys(draft[role])) {
          if (data.matryca[role]?.[upr] !== draft[role][upr]) {
            zmiany.push({ role, permission: upr, allowed: draft[role][upr] });
          }
        }
      }
      if (zmiany.length === 0) {
        setMsg({ type: 'ok', text: 'Brak zmian do zapisania' });
        setBusy(false);
        return;
      }
      const r = await admin.permissions.bulk(zmiany);
      setMsg({ type: 'ok', text: 'Zaktualizowano ' + r.zaktualizowane + ' pozycji matrycy' });
      await load();
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message });
    } finally { setBusy(false); }
  }

  type UprItem = { name: string; description: string; category: string };
  const kategorieUprawnien = useMemo(() => {
    if (!data) return [] as { category: string; items: UprItem[] }[];
    const grouped: Record<string, UprItem[]> = {};
    data.permissions.forEach((u: UprItem) => {
      grouped[u.category] = grouped[u.category] ?? [];
      grouped[u.category].push(u);
    });
    return Object.entries(grouped).map(([category, items]) => ({ category, items }));
  }, [data]);

  if (loading || !data) return <div className="text-ink-400">Ładowanie matrycy…</div>;

  return (
    <div className="max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Macierz uprawnień per role</h1>
          <div className="text-ink-400 text-[13px]">
            {data.role.length} ról × {data.permissions.length} uprawnień. Zmiany wchodzą w życie natychmiast (cache 60s).
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAddRole(v => !v)} className="btn btn-secondary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowa rola
          </button>
          <button onClick={() => setDraft(JSON.parse(JSON.stringify(data.matryca)))}
                  className="btn btn-secondary btn-sm">Cofnij zmiany</button>
          <button disabled={busy} onClick={() => void save()} className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/> Zapisz
          </button>
        </div>
      </header>

      {showAddRole && (
        <div className="card p-4 mb-4 flex flex-wrap items-end gap-3">
          <label className="field">
            <span className="field-label">Kod roli (A-Z, 0-9, _)</span>
            <input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())}
                   placeholder="np. BRYGADZISTA" className="input font-mono" />
          </label>
          <label className="field">
            <span className="field-label">Nazwa wyświetlana</span>
            <input value={newName} onChange={e => setNewName(e.target.value)}
                   placeholder="np. Brygadzista terenowy" className="input" />
          </label>
          <label className="flex items-center gap-2 text-[13px] text-ink-700 pb-2">
            <input type="checkbox" checked={newGV} onChange={e => setNewGV(e.target.checked)} className="w-4 h-4" />
            Globalny widz opracowań
          </label>
          <button disabled={busy} onClick={() => void addRole()} className="btn btn-primary btn-sm mb-1">Utwórz</button>
          <div className="text-[11px] text-ink-400 basis-full">
            Nowa rola powstaje bez uprawnień — zaznacz je w macierzy poniżej i zapisz, a potem przypisz rolę użytkownikom.
          </div>
        </div>
      )}

      {msg && (
        <div className={'mb-4 p-3 rounded text-[13px] ' +
          (msg.type === 'ok' ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500')}>
          {msg.text}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table-hw">
            <thead>
              <tr>
                <th className="min-w-[240px]">Permission</th>
                {data.role.map(r => (
                  <th key={r.name} className="text-center min-w-[100px]">
                    <div className="text-[11px]">{r.displayName}</div>
                    <div className="font-mono text-[10px] text-ink-400 mt-0.5">{r.name}</div>
                    <div className="flex items-center justify-center gap-1 mt-0.5">
                      {r.system
                        ? <span className="text-[9px] text-ink-300 uppercase tracking-wide">wbudowana</span>
                        : (
                          <button onClick={() => void delRole(r.name)} title="Usuń rolę"
                                  className="text-[10px] text-red-500 hover:text-red-500 hover:underline">usuń</button>
                        )}
                      <button onClick={() => void toggleGlobalViewer(r.name, r.globalViewer)}
                              title={r.globalViewer ? 'Globalny widz — kliknij, by wyłączyć' : 'Nadaj: widzi wszystkie opracowania'}
                              className={'text-[10px] ' + (r.globalViewer ? 'text-water-500' : 'text-ink-300 hover:text-ink-500')}>
                        {r.globalViewer ? '👁 wszystko' : '👁'}
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {kategorieUprawnien.map(grupa => (
                <>
                  <tr key={grupa.category} className="bg-ink-50">
                    <td colSpan={1 + data.role.length} className="font-semibold text-[11px] uppercase tracking-wider text-ink-600">
                      {grupa.category}
                    </td>
                  </tr>
                  {grupa.items.map(u => (
                    <tr key={u.name}>
                      <td>
                        <div className="font-mono text-[11px] text-ink-700">{u.name}</div>
                        <div className="text-[11px] text-ink-400">{u.description}</div>
                      </td>
                      {data.role.map(r => {
                        const checked = draft[r.name]?.[u.name] ?? false;
                        const original = data.matryca[r.name]?.[u.name] ?? false;
                        const changed = checked !== original;
                        return (
                          <td key={r.name} className="text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(r.name, u.name)}
                              className={'w-5 h-5 cursor-pointer ' + (changed ? 'ring-2 ring-amber-500 rounded' : '')}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-[11px] text-ink-400">
        💡 Pola z żółtą obwódką = niezapisane zmiany. OWNER zawsze ma pełen dostęp "globalny" do wszystkich opracowań;
        inne role widzą tylko te, do których zostały przypisane.
      </div>
    </div>
  );
}
